import { useState, useCallback } from 'react';
import { useCreateFieldMapping } from './useCreateFieldMapping';
import { useUpdateFieldMapping } from './useUpdateFieldMapping';
import { useDeleteFieldMapping } from './useDeleteFieldMapping';
import { MappingStatus, FieldMappingResponse } from '../types';
import { fieldMappingService } from '../services';
import { unsanitizeProjectFieldName } from '~/src/entities/project/utils';
import { useCurrentOrgFprint } from '~/src/entities/user';
import type { FieldMappingOverrideSubjectType } from '../services/fieldMappingService';

type UseFieldMappingSaveProps = {
  projectId: number;
  fieldId: string;
  fieldMappingId?: number;
  initialValue: string;
  selectedValue: string;
  overrideConfig?: {
    enabled: boolean;
    subjectType: FieldMappingOverrideSubjectType;
    subjectId: number;
  };
  onSuccess?: (response?: FieldMappingResponse) => void;
};

export const useFieldMappingSave = ({
  projectId,
  fieldId,
  fieldMappingId,
  initialValue,
  selectedValue,
  overrideConfig,
  onSuccess,
}: UseFieldMappingSaveProps) => {
  const [saveError, setSaveError] = useState<string | null>(null);
  const orgFprint = useCurrentOrgFprint();

  const createMutation = useCreateFieldMapping();
  const updateMutation = useUpdateFieldMapping();
  const deleteMutation = useDeleteFieldMapping();

  const isSaving =
    createMutation.isLoading ||
    updateMutation.isLoading ||
    deleteMutation.isLoading;

  const hasInitialValue = !!initialValue;
  const hasNewValue = !!selectedValue;
  const shouldSetOverride =
    !!overrideConfig?.enabled && !!fieldMappingId && !!orgFprint;

  const handleSaveChanges = useCallback(async () => {
    if (!hasInitialValue && !hasNewValue) {
      return;
    }

    setSaveError(null);

    try {
      const decodedTargetFieldId = unsanitizeProjectFieldName(fieldId);
      let response: FieldMappingResponse | undefined;

      if (hasInitialValue && !hasNewValue && fieldMappingId) {
        if (shouldSetOverride) {
          await fieldMappingService.setFieldMappingOverride({
            orgFprint,
            fieldMappingSubjectType: overrideConfig!.subjectType,
            fieldMappingSubjectId: overrideConfig!.subjectId,
            fieldMappingId,
            isOverride: false,
          });
        }

        await deleteMutation.mutateAsync({
          projectId,
          mappingId: fieldMappingId,
          clearValue: true,
        });
        response = undefined;
      } else if (!hasInitialValue && hasNewValue) {
        response = await createMutation.mutateAsync({
          projectId,
          data: {
            source_name: selectedValue,
            target_name: decodedTargetFieldId,
            status: MappingStatus.USER_CREATED,
          },
        });
      } else if (hasInitialValue && hasNewValue && fieldMappingId) {
        if (shouldSetOverride) {
          await fieldMappingService.setFieldMappingOverride({
            orgFprint,
            fieldMappingSubjectType: overrideConfig!.subjectType,
            fieldMappingSubjectId: overrideConfig!.subjectId,
            fieldMappingId,
            isOverride: false,
          });
        }

        response = await updateMutation.mutateAsync({
          projectId,
          mappingId: fieldMappingId,
          data: {
            source_name: selectedValue,
            status: MappingStatus.USER_MODIFIED,
          },
        });
      }

      onSuccess?.(response);
    } catch (err: any) {
      const errorMessage =
        err.message || 'Failed to save field mapping. Please try again.';
      setSaveError(errorMessage);
      console.error('Error saving field mapping:', err);
    }
  }, [
    hasInitialValue,
    hasNewValue,
    fieldId,
    fieldMappingId,
    shouldSetOverride,
    orgFprint,
    overrideConfig,
    projectId,
    selectedValue,
    deleteMutation,
    createMutation,
    updateMutation,
    onSuccess,
  ]);

  return {
    isSaving,
    saveError,
    handleSaveChanges,
  };
};
